/**
 * Date Filter — Inline order scheduler with day-of-the-week product filtering
 * Uses Shopify's Section Rendering API to fetch tag-filtered collection pages.
 *
 * Tag convention: Monday, Tuesday, Wednesday, Thursday, Friday, Saturday, Sunday
 * Products with NO day tags remain visible (always-available / staple items).
 *
 * Integrated with OrderScheduler: selecting options here updates
 * the session-wide pickup/delivery state used in cart & checkout.
 */
(function () {
  'use strict';

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var OS = window.OrderScheduler;

  var input = document.getElementById('date-filter-input');
  if (!input) return;

  // ── Per-product date blackout (both pickup AND delivery) ──────────────────
  // Some products are unavailable on specific dates regardless of order type
  // (e.g. Allergy-Friendly 6 Count Boxes on Sat July 18, 2026). The blocked
  // dates are emitted by date-filter-sidebar.liquid as
  // #date-filter-input[data-blackout-dates] (comma-separated YYYY-MM-DD). When
  // present, we wrap OrderScheduler's two date helpers so the picker disables
  // those dates for EVERY order type and never defaults/bumps onto them —
  // every existing picker path (init, order-type switch, auto-bump) then
  // respects the blackout with no further changes. Empty on all other pages,
  // so nothing else is affected. Does NOT touch the Box Builder.
  (function applyProductDateBlackout() {
    if (!OS) return;
    var blackout = (input.getAttribute('data-blackout-dates') || '')
      .split(',').map(function (s) { return s.trim(); }).filter(Boolean);
    if (!blackout.length) return;
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function ymd(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
    function isBlackout(d) { return blackout.indexOf(ymd(d)) !== -1; }
    var origDisable = OS.getDisabledDateFn.bind(OS);
    OS.getDisabledDateFn = function (orderType) {
      var base = origDisable(orderType);
      return function (date) { return isBlackout(date) || base(date); };
    };
    var origFirst = OS.getFirstAvailableDate.bind(OS);
    OS.getFirstAvailableDate = function () {
      var fn = OS.getDisabledDateFn();
      var d = new Date(OS.getMinDate());
      d.setHours(0, 0, 0, 0);
      for (var i = 0; i < 28; i++) { if (!fn(d)) return d; d.setDate(d.getDate() + 1); }
      return origFirst();
    };
    // Drop a stale stored date that's now blacked out (e.g. carried over from
    // another product this session) so the default-date logic re-picks a valid one.
    var stored = OS.getDate && OS.getDate();
    if (stored && window.flatpickr && typeof window.flatpickr.parseDate === 'function') {
      var sd = window.flatpickr.parseDate(stored, 'd F Y');
      if (sd && isBlackout(sd)) OS.clearDate();
    }
  })();

  var container     = document.getElementById('os-inline-bar');
  var clearBtn      = document.getElementById('date-filter-clear');
  var statusEl      = document.getElementById('date-filter-status');
  var loadingEl     = document.getElementById('date-filter-loading');
  var summaryEl     = document.getElementById('os-inline-summary');
  var fieldsEl      = document.getElementById('os-inline-fields');
  var timeStep      = document.getElementById('os-inline-time-step');
  var timeSelect    = document.getElementById('os-inline-time-select');
  var pickupTimeStep   = document.getElementById('os-inline-pickup-time-step');
  var pickupTimeSelect = document.getElementById('os-inline-pickup-time-select');
  var typeButtons   = document.querySelectorAll('.os-inline-type-btn');

  var sectionId     = container ? container.getAttribute('data-section-id') : null;
  var collectionUrl = container ? container.getAttribute('data-collection-url') : null;

  // Determine the section ID from the product grid if not set via attribute
  if (!sectionId) {
    var gridEl = document.getElementById('product-grid');
    if (gridEl) sectionId = gridEl.getAttribute('data-id');
  }

  // Store the original page HTML so we can restore on clear
  var originalGridHTML = null;
  var productGridContainer = document.getElementById('ProductGridContainer');
  if (productGridContainer) {
    originalGridHTML = productGridContainer.innerHTML;
  }

  // The current active day tag (null when no filter)
  var activeDay = null;

  // ── Flatpickr init (with date restrictions from OrderScheduler) ──────────
  var fp = flatpickr(input, {
    dateFormat: 'd F Y',           // "14 February 2026"
    minDate: OS ? OS.getMinDate() : 'today',
    allowInput: false,
    disableMobile: true,
    disable: OS ? [OS.getDisabledDateFn()] : [
      function (date) {
        var day = date.getDay();
        return day === 0 || day === 1;
      }
    ],
    onChange: function (selectedDates) {
      if (selectedDates.length > 0) {
        onDateSelected(selectedDates[0]);
      }
    }
  });

  // ── Default to the first available date when nothing is stored yet ───────
  // Prevents the picker from showing today (which is never actually selectable
  // due to the 24h/48h lead time + Sun/Mon closure). Persists to OrderScheduler
  // so the date carries through the cart pipeline.
  if (OS && !OS.getDate()) {
    var firstAvail = OS.getFirstAvailableDate();
    if (firstAvail) {
      fp.setDate(firstAvail, false); // false = skip onChange (no day-tag fetch needed yet)
      input.value = fp.formatDate(firstAvail, 'd F Y');
      OS.setDate(input.value, firstAvail);
    }
  }

  // ── Order-type buttons ──────────────────────────────────────────────────
  typeButtons.forEach(function (btn) {
    btn.addEventListener('click', function () {
      // Clear any previously selected time when switching modes — delivery and pickup
      // have different windows (11–3 vs 10–6), so a stale selection could land outside.
      if (OS && OS.getType() && OS.getType() !== btn.dataset.type) {
        OS.clearTime();
        if (timeSelect) timeSelect.value = '';
        if (pickupTimeSelect) pickupTimeSelect.value = '';
      }
      if (OS) {
        OS.setType(btn.dataset.type);
        // Re-apply the order-type-aware disable rule so delivery-only blackout
        // days grey out when Delivery is chosen (and re-open for Pickup). If the
        // selected date just became unavailable for the new type, bump it to the
        // next available day (which also refreshes the day-of-week product filter).
        if (fp) {
          var disableFn = OS.getDisabledDateFn();
          fp.set('disable', [disableFn]);
          var curr = fp.selectedDates && fp.selectedDates[0];
          if (curr && disableFn(curr)) {
            var nextAvail = OS.getFirstAvailableDate();
            if (nextAvail) {
              fp.setDate(nextAvail, false); // false = skip onChange; we call onDateSelected explicitly
              input.value = fp.formatDate(nextAvail, 'd F Y');
              onDateSelected(nextAvail);
            }
          }
        }
      }
      syncInlineUI();
    });
  });

  // ── Time selects (delivery 11–3, pickup 10–6) ───────────────────────────
  if (timeSelect) {
    timeSelect.addEventListener('change', function () {
      if (OS) {
        OS.setTime(timeSelect.value);
      }
      syncInlineUI();
    });
  }
  if (pickupTimeSelect) {
    pickupTimeSelect.addEventListener('change', function () {
      if (OS) {
        OS.setTime(pickupTimeSelect.value);
      }
      syncInlineUI();
    });
  }

  // ── If state was previously stored in the session, restore it ───────────
  if (OS) {
    // Restore order type
    syncInlineUI();

    if (OS.getDate()) {
      input.value = OS.getDate();
      var storedDay = OS.getDayName();
      if (storedDay) {
        activeDay = storedDay;
        showClearButton(true);
        setStatus('Showing items for ' + storedDay);
        // Apply the filter if on a collection page
        if (collectionUrl) {
          fetchFilteredCollection(storedDay);
        }
      }
    }
  }

  // ── Clear button ──────────────────────────────────────────────────────────
  if (clearBtn) {
    clearBtn.addEventListener('click', function () {
      fp.clear();
      if (timeSelect) timeSelect.value = '';
      if (pickupTimeSelect) pickupTimeSelect.value = '';
      clearFilter();
      if (OS) {
        OS.clearAll();
      }
      syncInlineUI();
    });
  }

  // ── Date selected → determine day → fetch filtered section ───────────────
  function onDateSelected(date) {
    var dayName = DAY_NAMES[date.getDay()];
    activeDay = dayName;

    // Sync with session-wide OrderScheduler
    if (OS) {
      OS.setDate(fp.formatDate(date, 'd F Y'), date);
    }

    showClearButton(true);
    setStatus('Showing items for ' + dayName);
    setLoading(true);

    // Only filter collection if on a collection page
    if (collectionUrl) {
      fetchFilteredCollection(dayName);
    } else {
      setLoading(false);
    }

    syncInlineUI();
  }

  // ── Clear → restore original grid ────────────────────────────────────────
  function clearFilter() {
    activeDay = null;
    showClearButton(false);
    setStatus('');
    setLoading(false);

    // Sync with session-wide OrderScheduler
    if (OS) {
      OS.clearDate();
    }

    // Restore original unfiltered grid
    if (originalGridHTML && productGridContainer) {
      productGridContainer.innerHTML = originalGridHTML;
      reinitPageFeatures();
    }
  }

  // ── Listen for changes from the popup scheduler ─────────────────────────
  document.addEventListener('order-scheduler:confirmed', function () {
    if (!OS) return;
    var day = OS.getDayName();
    var dateStr = OS.getDate();
    if (day && day !== activeDay) {
      activeDay = day;
      input.value = dateStr;
      showClearButton(true);
      setStatus('Showing items for ' + day);
      setLoading(true);
      if (collectionUrl) {
        fetchFilteredCollection(day);
      } else {
        setLoading(false);
      }
    }
    syncInlineUI();
  });

  // ── Listen for scheduler changes (from popup or other sources) ──────────
  document.addEventListener('order-scheduler:change', function () {
    syncInlineUI();
    // Apply day-of-week + order-type visibility on every change so the
    // pickup window shrinks to 10AM-4PM on Saturday, expands to 10AM-6PM
    // Tue-Fri, etc. (Mike + Renee + Chris 2026-05-19.)
    OS.applyTimeSlotVisibility();
  });

  // ── Sync inline UI state from sessionStorage ────────────────────────────
  function syncInlineUI() {
    if (!OS) return;

    var currentType = OS.getType();

    // Type buttons
    typeButtons.forEach(function (btn) {
      btn.classList.toggle('os-btn--active', btn.dataset.type === currentType);
    });

    // Date input
    if (OS.getDate() && !input.value) {
      input.value = OS.getDate();
    }

    // Show/hide time step based on order type.
    // Delivery → 11–3 picker; Pickup → 10–6 picker; neither → both hidden.
    if (currentType === 'Delivery') {
      if (timeStep) timeStep.style.display = '';
      if (pickupTimeStep) pickupTimeStep.style.display = 'none';
      if (fieldsEl) fieldsEl.classList.remove('os-inline__fields--no-time');
      if (timeSelect && OS.getTime() && timeSelect.value !== OS.getTime()) {
        timeSelect.value = OS.getTime();
      }
    } else if (currentType === 'Pickup') {
      if (timeStep) timeStep.style.display = 'none';
      if (pickupTimeStep) pickupTimeStep.style.display = '';
      if (fieldsEl) fieldsEl.classList.remove('os-inline__fields--no-time');
      if (pickupTimeSelect && OS.getTime() && pickupTimeSelect.value !== OS.getTime()) {
        pickupTimeSelect.value = OS.getTime();
      }
    } else {
      if (timeStep) timeStep.style.display = 'none';
      if (pickupTimeStep) pickupTimeStep.style.display = 'none';
      if (fieldsEl) fieldsEl.classList.add('os-inline__fields--no-time');
    }

    // Summary display
    if (OS.isReady()) {
      if (summaryEl) {
        summaryEl.textContent = OS.getSummary();
        summaryEl.classList.add('is-visible');
      }
    } else {
      if (summaryEl) {
        summaryEl.classList.remove('is-visible');
      }
    }

    // Show clear button when any selection is made
    if (OS.getDate() || OS.getType() || OS.getTime()) {
      showClearButton(true);
    }
  }

  // ── Section Rendering API fetch ──────────────────────────────────────────
  function fetchFilteredCollection(dayTag) {
    if (!collectionUrl || !sectionId) {
      // Fallback: no collection context → do client-side filtering
      clientSideFilter(dayTag);
      setLoading(false);
      return;
    }

    var existingParams = window.location.search ? window.location.search.slice(1) : '';
    var tagUrl = collectionUrl + '/' + encodeURIComponent(dayTag);
    var separator = existingParams ? '&' : '';
    var fetchUrl = tagUrl + '?section_id=' + sectionId + separator + existingParams;

    fetch(fetchUrl)
      .then(function (response) {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var newGrid = parsed.getElementById('ProductGridContainer');

        if (newGrid && productGridContainer) {
          productGridContainer.innerHTML = newGrid.innerHTML;
          mergeAlwaysAvailable(dayTag);
          reinitPageFeatures();
        }
        setLoading(false);
      })
      .catch(function () {
        if (originalGridHTML && productGridContainer) {
          productGridContainer.innerHTML = originalGridHTML;
        }
        clientSideFilter(dayTag);
        setLoading(false);
      });
  }

  // ── Merge "always available" products back into the filtered grid ────────
  function mergeAlwaysAvailable(dayTag) {
    if (!originalGridHTML) return;

    var originalDoc = new DOMParser().parseFromString(
      '<div id="wrap">' + originalGridHTML + '</div>', 'text/html'
    );
    var originalCards = originalDoc.querySelectorAll('product-card[data-day-tags]');
    var currentGrid = productGridContainer.querySelector('#product-grid');
    if (!currentGrid) return;

    var loadingOverlay = currentGrid.querySelector('li.loading-overlay');

    originalCards.forEach(function (card) {
      var tags = (card.getAttribute('data-day-tags') || '').trim();

      if (tags === '') {
        var link = card.querySelector('.product-card-title');
        if (!link) return;
        var href = link.getAttribute('href');
        var alreadyExists = currentGrid.querySelector('.product-card-title[href="' + href + '"]');
        if (alreadyExists) return;

        var li = card.closest('li');
        if (li) {
          var clone = li.cloneNode(true);
          if (loadingOverlay) {
            currentGrid.insertBefore(clone, loadingOverlay);
          } else {
            currentGrid.appendChild(clone);
          }
        }
      }
    });
  }

  // ── Client-side fallback ──────────────────────────────────────────────────
  function clientSideFilter(dayTag) {
    var cards = document.querySelectorAll('product-card[data-day-tags]');

    cards.forEach(function (card) {
      var tags = (card.getAttribute('data-day-tags') || '').trim();
      var wrapper = card.closest('li, .carousel__slide, .collection-tabs--product') || card;

      if (tags === '') {
        wrapper.style.display = '';
      } else {
        var tagList = tags.split(',').map(function (d) { return d.trim(); });
        if (tagList.indexOf(dayTag) !== -1) {
          wrapper.style.display = '';
        } else {
          wrapper.style.display = 'none';
        }
      }
    });
  }

  // ── Re-init theme features after DOM replacement ────────────────────────
  function reinitPageFeatures() {
    if (typeof lazySizes !== 'undefined') {
      productGridContainer.querySelectorAll('.lazyload').forEach(function (img) {
        lazySizes.loader.unveil(img);
      });
    }
    showClearButton(!!activeDay);
  }

  // ── UI helpers ───────────────────────────────────────────────────────────
  function showClearButton(show) {
    if (clearBtn) {
      clearBtn.classList.toggle('is-visible', show);
    }
  }

  function setStatus(text) {
    if (statusEl) statusEl.textContent = text;
  }

  function setLoading(show) {
    if (loadingEl) {
      loadingEl.classList.toggle('is-visible', show);
    }
    var collection = productGridContainer
      ? productGridContainer.querySelector('.collection')
      : null;
    if (collection) {
      collection.classList.toggle('loading', show);
    }
  }
})();
